import { useCallback, useState } from "react";

const STORAGE_KEY = "crm-collapsed-sections";

const readStoredIds = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id) => typeof id === "string")
      : [];
  } catch {
    // private windows and blocked site data both throw; fall back to everything expanded
    return [];
  }
};

const writeStoredIds = (ids: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // remembering the state is a convenience, never a requirement
  }
};

/**
 * Which CRM sections the viewer has collapsed, remembered in their own browser. Collapsed ids
 * are stored rather than open ones so a section added later shows up expanded by default.
 */
export const useCollapsedCrmSections = () => {
  const [collapsedIds, setCollapsedIds] = useState<string[]>(readStoredIds);

  const persist = useCallback((ids: string[]) => {
    setCollapsedIds(ids);
    writeStoredIds(ids);
  }, []);

  const isCollapsed = useCallback(
    (sectionId: string) => collapsedIds.includes(sectionId),
    [collapsedIds]
  );

  const toggle = useCallback(
    (sectionId: string, shouldOpen: boolean) =>
      persist(
        shouldOpen
          ? collapsedIds.filter((id) => id !== sectionId)
          : [...collapsedIds.filter((id) => id !== sectionId), sectionId]
      ),
    [collapsedIds, persist]
  );

  const collapseAll = useCallback(
    (sectionIds: string[]) =>
      persist(Array.from(new Set([...collapsedIds, ...sectionIds]))),
    [collapsedIds, persist]
  );

  const expandAll = useCallback(
    (sectionIds: string[]) =>
      persist(collapsedIds.filter((id) => !sectionIds.includes(id))),
    [collapsedIds, persist]
  );

  return { isCollapsed, toggle, collapseAll, expandAll };
};
